import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

export default function Header() {
  const { t, i18n } = useTranslation();
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const categories = t("categories", { returnObjects: true }) as Array<{
    name: string;
    slug: string;
    url: string;
  }>;

  const languages = [
    { code: "en", label: "English" },
    { code: "pt", label: "Português" },
    { code: "de", label: "Deutsch" },
  ];

  const currentLang = i18n.language;

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setMobileCatOpen(false);
  }, [location.pathname]);

  function switchLanguage(code: string) {
    const path = location.pathname;
    const newPath = path.replace(/^\/(en|pt|de)?/, `/${code}`);
    i18n.changeLanguage(code);
    navigate(newPath || `/${code}`);
    setLangOpen(false);
    setMobileOpen(false);
  }

  function getHomePath() {
    return `/${currentLang}`;
  }

  function getShoppingGuidePath() {
    return `/${currentLang}/article/cssbuy-buying-guide`;
  }

  function getArticlePath(slug: string) {
    return `/${currentLang}/article/${slug}`;
  }

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={getHomePath()} className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 rounded-lg bg-emerald-700 flex items-center justify-center">
              <i className="ri-article-line text-white text-lg" />
            </div>
            <span className="font-bold text-lg text-gray-900 tracking-tight whitespace-nowrap">
              {t("siteName")}
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-5">
            <Link
              to={getHomePath()}
              className="text-sm font-semibold text-gray-900 hover:text-emerald-700 transition-colors whitespace-nowrap"
            >
              {t("nav.home")}
            </Link>

            <div className="relative" ref={catRef}>
              <button
                onClick={() => setCatOpen(!catOpen)}
                className="flex items-center gap-1 text-sm font-semibold text-gray-900 hover:text-emerald-700 transition-colors whitespace-nowrap"
              >
                {t("nav.categories")}
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`} />
              </button>
              {catOpen && (
                <div className="absolute top-full left-0 mt-2 w-60 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 grid grid-cols-2 gap-1 px-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-3 py-2 rounded-md text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors whitespace-nowrap"
                      onClick={() => setCatOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to={`/${currentLang}/products`}
              className="text-sm font-semibold text-gray-900 hover:text-emerald-700 transition-colors whitespace-nowrap"
            >
              {t("nav.products")}
            </Link>

            <Link
              to={getArticlePath("cssbuy-buying-guide")}
              className="text-sm font-semibold text-gray-900 hover:text-emerald-700 transition-colors whitespace-nowrap"
            >
              {t("nav.guides")}
            </Link>
            <Link
              to={getArticlePath("cssbuy-review-2026")}
              className="text-sm font-semibold text-gray-900 hover:text-emerald-700 transition-colors whitespace-nowrap"
            >
              {t("nav.reviews")}
            </Link>
            <Link
              to={getArticlePath("cssbuy-shipping-cost-2026")}
              className="text-sm font-semibold text-gray-900 hover:text-emerald-700 transition-colors whitespace-nowrap"
            >
              {t("nav.shipping")}
            </Link>
            <Link
              to={getArticlePath("cssbuy-qc-guide")}
              className="text-sm font-semibold text-gray-900 hover:text-emerald-700 transition-colors whitespace-nowrap"
            >
              {t("nav.safety")}
            </Link>

            {/* Shopping Guide CTA */}
            <Link
              to={getShoppingGuidePath()}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-700 text-white text-sm font-semibold rounded-lg hover:bg-emerald-800 transition-colors whitespace-nowrap"
            >
              <i className="ri-book-open-line" />
              {t("nav.shoppingGuide")}
            </Link>

            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1 text-sm font-semibold text-gray-900 hover:text-emerald-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-global-line" />
                {languages.find((l) => l.code === currentLang)?.label}
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? "rotate-180" : ""}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => switchLanguage(lang.code)}
                      className={`block w-full text-left px-4 py-2 text-sm transition-colors ${
                        currentLang === lang.code
                          ? "bg-emerald-50 text-emerald-700 font-medium"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg text-gray-900 hover:bg-gray-100"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 px-4 py-4 max-h-[80vh] overflow-y-auto">
          {/* Primary actions */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <Link
              to={getHomePath()}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-900 font-semibold rounded-lg"
            >
              <i className="ri-home-4-line" />
              {t("nav.home")}
            </Link>
            <Link
              to={getShoppingGuidePath()}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-emerald-700 text-white font-semibold rounded-lg"
            >
              <i className="ri-book-open-line" />
              {t("nav.shoppingGuide")}
            </Link>
          </div>

          {/* Secondary nav links */}
          <div className="space-y-1 mb-3">
            <Link to={getArticlePath("cssbuy-buying-guide")} className="block px-3 py-2.5 rounded-lg text-sm font-semibold text-gray-900 hover:bg-gray-50">
              {t("nav.guides")}
            </Link>
            <Link to={`/${currentLang}/products`} className="block px-3 py-2.5 rounded-lg text-sm font-semibold text-gray-900 hover:bg-gray-50">
              {t("nav.products")}
            </Link>
            <Link to={getArticlePath("cssbuy-review-2026")} className="block px-3 py-2.5 rounded-lg text-sm font-semibold text-gray-900 hover:bg-gray-50">
              {t("nav.reviews")}
            </Link>
            <Link to={getArticlePath("cssbuy-shipping-cost-2026")} className="block px-3 py-2.5 rounded-lg text-sm font-semibold text-gray-900 hover:bg-gray-50">
              {t("nav.shipping")}
            </Link>
            <Link to={getArticlePath("cssbuy-qc-guide")} className="block px-3 py-2.5 rounded-lg text-sm font-semibold text-gray-900 hover:bg-gray-50">
              {t("nav.safety")}
            </Link>
          </div>

          {/* Categories accordion */}
          <button
            onClick={() => setMobileCatOpen(!mobileCatOpen)}
            className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg bg-gray-50 text-sm font-semibold text-gray-900 mb-2"
          >
            <span className="flex items-center gap-2">
              <i className="ri-grid-line text-emerald-700" />
              {t("nav.categories")}
            </span>
            <i className={`ri-arrow-down-s-line transition-transform ${mobileCatOpen ? "rotate-180" : ""}`} />
          </button>
          {mobileCatOpen && (
            <div className="grid grid-cols-2 gap-1.5 mb-3">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="block px-3 py-2 rounded-lg text-sm text-gray-700 bg-gray-50 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          )}

          {/* Language */}
          <div className="pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-2">{t("nav.language")}</p>
            <div className="flex gap-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => switchLanguage(lang.code)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                    currentLang === lang.code
                      ? "bg-emerald-700 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}